"use client";

import { useEffect, useRef } from "react";

/**
 * Adds `is-visible` to the returned ref's element the first time it scrolls
 * into view, then stops observing. Sections below the fold use this to start
 * their entrance keyframes on arrival instead of on page load (the hero, which
 * is the LCP element, deliberately does the opposite — see `jd-fade-up`).
 *
 * The class goes on one wrapper; children stagger themselves off it with
 * per-element `animation-delay`, so a single observer drives a whole section.
 */
export function useRevealOnce<T extends HTMLElement>(
  /** How much of the element must be on screen before it fires. */
  threshold = 0.25,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // No IntersectionObserver (or reduced motion): show the final state now.
    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      el.classList.add("is-visible");
      return;
    }

    // A section taller than `1 / threshold` viewports can never reach the
    // requested ratio, so its observer simply never fires and the section
    // stays at its pre-entrance opacity — i.e. blank. That is not theoretical:
    // the bento grid stacks to 2361px on a 375x812 phone, capping the ratio at
    // 0.344 against a threshold of 0.35, and all six cards stayed invisible.
    //
    // Clamping to what a viewport-and-a-bit of the element is actually worth
    // keeps the intent ("a good chunk of it is on screen") reachable at any
    // height. Sections shorter than the viewport clamp to a value above their
    // own threshold and so keep exactly the one they asked for.
    const reachable = window.innerHeight / el.getBoundingClientRect().height;
    const effective = Math.min(threshold, reachable * 0.6);

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add("is-visible");
        io.disconnect();
      },
      { threshold: effective },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return ref;
}
